import { WaitingListItem } from '../../../models';
import { PlaceholderContext, resolvePlaceholders } from '../../../shared/placeholders';

export const getWaitingItemsForDeletion = (
    items: WaitingListItem[],
    service?: string,
    professionalId?: string,
): WaitingListItem[] => {
    if (!service) return [];

    return items.filter(item =>
        item.service === service && (!item.professionalId || item.professionalId === professionalId)
    );
};

export interface WaitingListClientContact {
    id: string;
    phoneNumbers?: string[];
    email?: string;
}

export const getPreferredContactFromClient = (
    client?: WaitingListClientContact,
): string | undefined => {
    if (!client) return undefined;

    const phone = client.phoneNumbers?.find(p => !!p?.trim()?.length)?.trim();
    if (phone) return phone;

    const email = client.email?.trim();
    return email?.length ? email : undefined;
};

export const hasPhoneNumberForClient = (
    client?: WaitingListClientContact,
): boolean => {
    if (!client) return false;
    return !!client.phoneNumbers?.some(phone => !!phone?.trim()?.length);
};

export const getPreferredClientContact = (
    clients: WaitingListClientContact[],
    clientId?: string,
): string | undefined => {
    if (!clientId) return undefined;
    return getPreferredContactFromClient(clients.find(c => c.id === clientId));
};

export const hasClientPhoneNumber = (
    clients: WaitingListClientContact[],
    clientId?: string,
): boolean => {
    if (!clientId) return false;
    return hasPhoneNumberForClient(clients.find(c => c.id === clientId));
};

export const buildWaitingListSmsMessage = (
    template: string,
    context: PlaceholderContext,
): string => resolvePlaceholders(template, context);

export const getWaitingListSmsButtonTooltip = (
    smsPreviewMessage: string,
    hasClientPhone: boolean,
    smsClientPhoneRequiredLabel: string,
): string => {
    if (!hasClientPhone) return smsClientPhoneRequiredLabel;
    return smsPreviewMessage;
};

export interface CreateAppointmentAndSendSmsFlowParams {
    item: WaitingListItem;
    hasClientPhone: (clientId?: string) => boolean;
    closeWaitingListAndDelete: (item: WaitingListItem) => Promise<void>;
    getWaitingListSmsMessage: (item: WaitingListItem) => string;
    sendSms: (clientId: string, message: string) => Promise<void>;
    onSuccess: () => void;
    onError: (error: unknown) => void;
}

export const createAppointmentAndSendSmsFlow = async ({
    item,
    hasClientPhone,
    closeWaitingListAndDelete,
    getWaitingListSmsMessage,
    sendSms,
    onSuccess,
    onError,
}: CreateAppointmentAndSendSmsFlowParams): Promise<void> => {
    if (!hasClientPhone(item.clientId)) {
        return;
    }

    await closeWaitingListAndDelete(item);

    if (!item.clientId) {
        return;
    }

    const message = getWaitingListSmsMessage(item);
    try {
        await sendSms(item.clientId, message);
        onSuccess();
    } catch (error) {
        onError(error);
    }
};
